import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { IErrors, IPolicy, IPolicyCategoryInfo, IPackageFormatGroup, IPackageFormatsResponse } from './models';
import UploadField from "./UploadField";
import { useState, useEffect, useCallback, useRef } from 'react';
import { apiGet } from "./api";
import './PortalForm.css';
import { InputField } from './components/input';
import { TextareaField } from './components/textarea';
import { SwitchField } from './components/checkbox';
import { SelectField } from './components/select';

interface IPortalFormProps {
    policy: IPolicy,
    onChange: ((name: string, value: any, type_field?: string) => void),
    canChange: boolean,
    errors: IErrors
    hasMdm: boolean,
}

function PortalForm({ policy, onChange, canChange, errors, hasMdm }: IPortalFormProps) {
    const [iconUrl, setIconUrl] = useState<string>(policy.app_icon?.url || "");
    const [selectedOS, setSelectedOS] = useState(policy.os_name || '');
    const [appPackageFormat, setAppPackageFormat] = useState(policy.vars.app_store_package_format || '');
    const [windowsArchitecture, setWindowsArchitecture] = useState(policy.vars.app_store_windows_architecture || '');
    const [appCategory, setAppCategory] = useState<number | string>(policy.app_category || '');
    const [categories, setCategories] = useState<IPolicyCategoryInfo[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [modalKey, setModalKey] = useState(0);

    const [modalAction, setModalAction] = useState<'add' | 'edit' | 'delete'>('add');
    const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);

    const [packageFormats, setPackageFormats] = useState<IPackageFormatGroup[]>([]);

    // Refs for iframe monitoring
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const hasFormsRef = useRef<boolean>(false);

    const availableOS = [
        { value: 'windows', label: 'Windows' },
        { value: 'linux', label: 'Linux' },
        { value: 'macos', label: 'MacOS' },
        ...(hasMdm ? [{ value: 'android', label: 'Android' }] : [])
    ];

    const fetchPackageFormats = useCallback(async () => {
        try {
            const response = await apiGet<IPackageFormatsResponse>('/automation/api/policy/package_formats/');
            setPackageFormats(response?.package_formats || []);
        } catch (error) {
            console.error('Failed to fetch package formats:', error);
            setPackageFormats([]);
        }
    }, []);

    useEffect(() => {
        fetchPackageFormats();
    }, [fetchPackageFormats]);


    const updateFieldShow = (ev: any) => {
        onChange("show_on_portal", ev.target.checked);
    }

    useEffect(() => {
        if (policy.show_in_app_store) {
            onChange("show_on_portal", true);
        }
        // eslint-disable-next-line
    }, [policy.show_in_app_store]);

    const updateShowInAppStore = (ev: any) => {
        onChange("show_in_app_store", ev.target.checked);
    }

    const updateFieldAllowExecute = (ev: any) => {
        onChange("auto_execute", ev.target.checked);
    }

    const updateFieldDescription = (ev: any) => {
        onChange("app_description", ev.target.value);
    }

    const updateFieldAppName = (ev: any) => {
        onChange("app_name", ev.target.value);
    }

    const updateFieldIcon = (name: string, file: any) => {
        onChange("app_icon", file);
        setIconUrl(file.url)
    }

    const DeleteFieldIcon = (name: string, _guid: any) => {
        onChange("app_icon", null);
        setIconUrl("")
    }

    useEffect(() => {
        setSelectedOS(policy.os_name || '');
    }, [policy.os_name]);

    useEffect(() => {
        setAppPackageFormat(policy.vars.app_store_package_format || '');
    }, [policy.vars.app_store_package_format]);

    useEffect(() => {
        setWindowsArchitecture(policy.vars.app_store_windows_architecture || '');
    }, [policy.vars.app_store_windows_architecture]);

    useEffect(() => {
        setAppCategory(policy.app_category || '');
    }, [policy.app_category]);

    const fetchCategories = useCallback(async () => {
        try {
            const fetchedCategories = await apiGet<IPolicyCategoryInfo[]>('/automation/api/app_categories/');
            setCategories(fetchedCategories);
            return fetchedCategories;
        } catch (error) {
            console.error('Failed to fetch categories:', error);
            setCategories([]);
            return [];
        }
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);



    const handleCloseModal = useCallback(async () => {
        setShowModal(false);
        setModalAction('add');
        setEditingCategoryId(null);

        // Reset form detection flag
        hasFormsRef.current = false;

        setTimeout(async () => {
            const prevCount = categories.length;
            const updated = await fetchCategories();

            if (updated.length > prevCount) {
                // autoselect new category
                const newest = updated[updated.length - 1];
                setAppCategory(newest.id);
                onChange('app_category', newest.id);
            } else if (appCategory && !updated.find(c => c.id === appCategory)) {
                // reset deleted category
                setAppCategory('');
                onChange('app_category', '');
            }
        }, 500);
    }, [categories.length, fetchCategories, onChange, appCategory]);

    // Handle opening the add category modal
    const handleAddCategory = () => {
        setModalAction('add');
        setEditingCategoryId(null);
        setModalKey(prev => prev + 1);
        setShowModal(true);
        hasFormsRef.current = false;
    };

    // Handle opening the edit category modal
    const handleEditCategory = () => {
        if (!appCategory || typeof appCategory !== 'number') {
            return;
        }

        setModalAction('edit');
        setEditingCategoryId(appCategory);
        setModalKey(prev => prev + 1);
        setShowModal(true);
        hasFormsRef.current = false;
    };

    // Handle opening the delete category modal
    const handleDeleteCategory = () => {
        if (!appCategory || typeof appCategory !== 'number') {
            return;
        }

        setModalAction('delete');
        setEditingCategoryId(appCategory);
        setModalKey(prev => prev + 1);
        setShowModal(true);
        hasFormsRef.current = false;
    };

    const monitorIframe = useCallback(() => {
        if (!iframeRef.current || !showModal) return;

        try {
            const iframe = iframeRef.current;
            const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;

            if (iframeDoc) {
                const forms = iframeDoc.querySelectorAll('form');
                const submitButtons = iframeDoc.querySelectorAll('input[type="submit"], button[type="submit"]');

                if ((forms.length > 0 || submitButtons.length > 0) && !hasFormsRef.current) {
                    hasFormsRef.current = true;
                }

                if (hasFormsRef.current && forms.length === 0 && submitButtons.length === 0) {
                    console.log('Form submission detected via missing form elements');
                    handleCloseModal();
                    return;
                }
            }
        } catch (error) {
            console.log('Cannot access iframe content due to cross-origin restrictions');
        }
    }, [showModal, handleCloseModal]);

    // Set up iframe monitoring
    useEffect(() => {
        if (!showModal) return;

        const interval = setInterval(monitorIframe, 1000);

        return () => {
            clearInterval(interval);
        };
    }, [showModal, monitorIframe]);

    const getModalTitle = () => {
        switch (modalAction) {
            case 'add':
                return 'Добавить новую категорию';
            case 'edit':
                return 'Изменить категорию';
            case 'delete':
                return 'Удалить категорию';
            default:
                return 'Добавить новую категорию';
        }
    };

    const getIframeUrl = () => {
        switch (modalAction) {
            case 'add':
                return "/automation/policycategoryinfo/add/?_popup=1";
            case 'edit':
                return `/automation/policycategoryinfo/${editingCategoryId}/change/?_popup=1`;
            case 'delete':
                return `/automation/policycategoryinfo/${editingCategoryId}/delete/?_popup=1`;
            default:
                return "/automation/policycategoryinfo/add/?_popup=1";
        }
    };

    return (
        <>
            <Form className="w-auto portal-form">
                <div>
                    <h4>Общие настройки</h4>
                    <div className="form-group-spacing">
                        <SwitchField
                            name="show_on_portal"
                            checked={policy.show_on_portal}
                            label='Показывать в разделе: "Портал самообслуживания"'
                            helperText='При создании "Обращения" будет возможность выбрать политику'
                            disabled={!canChange}
                            onChange={updateFieldShow}
                        />
                    </div>
                    <div className="pt-2">
                        <SwitchField
                            name="auto_execute"
                            checked={policy.auto_execute}
                            label="Автоматический запуск"
                            helperText="После создания обращения, политика запустится автоматически"
                            disabled={!canChange}
                            onChange={updateFieldAllowExecute}
                        />
                    </div>
                </div>
                <div className="pt-2">
                    <h4>Магазин приложений</h4>
                    <div className="form-group-spacing">
                        <SwitchField
                            name="show_in_app_store"
                            checked={policy.show_in_app_store}
                            label="Показывать в магазине приложений"
                            helperText="При выбранном чекбоксе политика будет отображаться в магазине приложений"
                            disabled={!canChange}
                            onChange={updateShowInAppStore}
                        />
                    </div>

                    <div className="pt-2">
                        <InputField
                            name="app_name"
                            value={policy.app_name || ''}
                            label="Название приложения"
                            helperText="Название будет отображено в магазине приложений"
                            error={errors.fields["app_name"]}
                            disabled={!canChange}
                            onChange={updateFieldAppName}
                        />
                    </div>

                    <div className="pt-2">
                        <TextareaField
                            name="app_description"
                            value={policy.app_description || ''}
                            label="Описание приложения"
                            helperText="Описание будет отображено в магазине приложений"
                            rows={3}
                            disabled={!canChange}
                            onChange={updateFieldDescription}
                        />
                    </div>

                    <div className="pt-2">
                        <div className="related-widget-wrapper">
                            <SelectField
                                mode="static"
                                name="app_category"
                                label="Категория приложения"
                                placeholder="Выберите категорию"
                                floating
                                size="medium"
                                options={categories.map(c => ({ value: String(c.id), label: c.name }))}
                                initialValue={appCategory ? String(appCategory) : ''}
                                disabled={!canChange}
                                error={errors.fields["app_category"]}
                                clearable
                                searchable
                                onChange={(value) => {
                                    const numValue = value ? parseInt(value, 10) : '';
                                    setAppCategory(numValue);
                                    onChange('app_category', numValue);
                                }}
                            />
                            {canChange && (
                                <>
                                    <button
                                        className="rd-btn rd-btn--medium rd-btn--outline rd-btn--primary rd-btn--icon-only related-widget-wrapper-link"
                                        type="button"
                                        onClick={handleAddCategory}
                                        title="Добавить новую категорию"
                                    >
                                        <i className="rd-icon-add" />
                                    </button>
                                    <button
                                        className="rd-btn rd-btn--medium rd-btn--outline rd-btn--primary rd-btn--icon-only related-widget-wrapper-link"
                                        type="button"
                                        onClick={handleEditCategory}
                                        disabled={!appCategory || typeof appCategory !== 'number'}
                                        title="Изменить выбранную категорию"
                                    >
                                        <i className="rd-icon-edit" />
                                    </button>
                                    <button
                                        className="rd-btn rd-btn--medium rd-btn--error rd-btn--outline rd-btn--icon-only related-widget-wrapper-link"
                                        type="button"
                                        onClick={handleDeleteCategory}
                                        disabled={!appCategory || typeof appCategory !== 'number'}
                                        title="Удалить выбранную категорию"
                                    >
                                        <i className="rd-icon-delete" />
                                    </button>
                                </>
                            )}
                        </div>
                        <span className="rd-input__helper-text">
                            Категория будет отображена в магазине приложений
                        </span>
                    </div>

                    <div className="pt-2">
                        <SelectField
                            mode="static"
                            name="os_name"
                            label="Выберите операционную систему"
                            floating
                            size="medium"
                            options={[
                                { value: '', label: 'Для всех ОС' },
                                ...availableOS.map(os => ({ value: os.value, label: os.label })),
                            ]}
                            initialValue={selectedOS}
                            disabled={!canChange}
                            error={errors.fields["os_name"]}
                            onChange={(value) => {
                                setSelectedOS(value);
                                onChange('os_name', value);
                            }}
                        />
                    </div>

                    {selectedOS === 'windows' && (
                        <div className="pt-2">
                            <SelectField
                                mode="static"
                                name="app_store_windows_architecture"
                                label="Архитектура Windows"
                                floating
                                size="medium"
                                options={[
                                    { value: '', label: 'Выберите архитектуру' },
                                    { value: 'all', label: 'Для всех архитектур' },
                                    { value: '64-bit', label: '64-bit' },
                                    { value: '32-bit', label: '32-bit' },
                                ]}
                                initialValue={windowsArchitecture}
                                disabled={!canChange}
                                error={errors.fields["app_store_windows_architecture"]}
                                onChange={(value) => {
                                    setWindowsArchitecture(value);
                                    onChange('app_store_windows_architecture', value);
                                }}
                            />
                        </div>
                    )}

                    {selectedOS === 'linux' && (
                        <div className="pt-2">
                            <SelectField
                                mode="static"
                                name="app_store_package_format"
                                label="Формат установочного пакета"
                                placeholder="Выберите формат"
                                floating
                                size="medium"
                                options={packageFormats.map(group => ({
                                    label: group.group,
                                    options: group.formats.map(f => ({ value: f.value, label: f.label })),
                                }))}
                                initialValue={appPackageFormat}
                                disabled={!canChange}
                                error={errors.fields["app_store_package_format"]}
                                searchable
                                onChange={(value) => {
                                    setAppPackageFormat(value);
                                    onChange('app_store_package_format', value);
                                }}
                            />
                            <span className="rd-input__helper-text">
                                Нажмите на поле для выбора из доступных форматов
                            </span>
                            {canChange && (
                                <button
                                    className="rd-btn rd-btn--medium rd-btn--secondary rd-btn--outline"
                                    type="button"
                                    onClick={() => window.open(`${window.location.origin}/automation/linuxdistribution/`, '_blank')}
                                    style={{ marginTop: '10px' }}
                                >
                                    Дистрибутивы Linux и поддерживаемые форматы
                                </button>
                            )}
                        </div>
                    )}

                    <Form.Group className={"pt-2"}>
                        <div className="card">
                            <h5 className="card-header">Иконка приложения</h5>
                            <div className="card-body">
                                <UploadField
                                    name={"app_icon"}
                                    label={"some"}
                                    onSuccess={updateFieldIcon}
                                    onDelete={DeleteFieldIcon}
                                    files={policy.app_icon ? [policy.app_icon] : []}
                                    required={false}
                                    canChange={canChange}
                                    repository={true}
                                />
                                <img className="app-icon-image align-self-start mr-3" alt=""
                                    src={iconUrl} />
                                <Form.Text className="text-muted">Иконка будет отображена в магазине приложений. Рекомендуемый формат .svg</Form.Text>
                            </div>
                        </div>
                    </Form.Group>
                </div>
            </Form>

            <Modal
                show={showModal}
                onHide={handleCloseModal}
                size="lg"
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{getModalTitle()}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="modal-body-no-padding">
                    <iframe
                        ref={iframeRef}
                        key={modalKey}
                        src={getIframeUrl()}
                        className="modal-iframe"
                        title={getModalTitle()}
                        onLoad={() => {
                            // Start monitoring after iframe loads
                            setTimeout(monitorIframe, 1000);
                        }}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="rd-btn rd-btn--medium rd-btn--outline rd-btn--secondary"
                        onClick={handleCloseModal}
                    >
                        Закрыть
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default PortalForm;
